import DynamicSelect from "common/Select/Select";
import {
  Box,
  Button,
  Divider,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ReactComponent as ModalBtn } from "assets/Modal/ModalBtn.svg";
import BasicDatePicker from "common/Pickers/Pickers";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { useNavigate } from "react-router-dom";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { InventoryItemsTable } from "components/Inventory/InventoryItemsTable";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useGetVendorService } from "subModule/src/services/vendor/useVendor";
import { useGetStoreDropdownService } from "subModule/src/services/store/useStore";
import { useGetTaxProcedureDropdownService } from "subModule/src/services/taxProcedure/useTaxProcedure";
import {
  InventoryProfileService,
  VendorProfileService,
} from "subModule/src/core/services";
import { GetVendorByIdResponse } from "subModule/src/services/vendor/interface";
import { InventoryServicesTable } from "components/Inventory/InventoryServicesTable";
import { useGetItemsDropdownService } from "subModule/src/services/items/useItems";
import { useGetServicesDropdownService } from "subModule/src/services/service/useService";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import { NewPurchaseOrderResponse } from "subModule/src/services/inventory/interface";
import { useTranslation } from "react-i18next";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import FormDialog from "common/Modal/Modal";
import StoreModal from "pages/Store/StoreModal";
import TaxProcedureModal from "pages/TaxProcedure/TaxProcedureModal";
import SupplierModal from "pages/Supplier/SupplierModal";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import ServicesModal from "pages/Service/ServicesModal";
import { GetItemsDropdownResponse } from "subModule/src/services/items/interface";
import Loader from "layout/Loader";
import dayjs from "dayjs";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData {
  [key: string]: any;
}

export default function NewPurchase() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { breadcrumbItems } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vendors",
    moduleLabelKey: "Purchase Orders",
    moduleHref: "/inventory/purchase-orders",
    entityFallbackLabelKey: "Purchase Order Number",
    isEdit: false,
  });
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserCurrency = (useSelector(selectUserInfo) as any)?.business
    ?.currency;
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitSuccessful },
    watch,
    resetField,
  } = useForm<FormData>({
    defaultValues: {
      purchaseOrderItems: [],
      purchaseOrderSvc: [],
      stockType: 5,
    },
  });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: vendorsData, refetch: vendorsRefetch } = useGetVendorService({
    pageNumber: dropdownPage,
    size: 50,
    title: dropdownSearchTerm,
  });
  const { data: storesData, refetch: storesRefetch } =
    useGetStoreDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: taxProceduresData, refetch: taxProceduresRefetch } =
    useGetTaxProcedureDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: itemsData, refetch: itemsRefetch } = useGetItemsDropdownService(
    {
      pageNumber: dropdownPage,
      size: 20,
      storeId: watch("storeId")?.value,
      search: dropdownSearchTerm,
    },
  );
  const { data: servicesData, refetch: servicesRefetch } =
    useGetServicesDropdownService({
      pageNumber: dropdownPage,
      size: 20,
      search: dropdownSearchTerm,
    });
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [purchaseOrderItems, setPurchaseOrderItems] = useState<any[]>([]);
  const [purchaseOrderSvc, setPurchaseOrderSvc] = useState<any[]>([]);
  const [addNew, setAddNew] = useState<string>("");
  const [taxPercentage, setTaxPercentage] = useState<number>(0);
  const [partsLoader, setPartsLoader] = useState<boolean>(false);
  useEffect(() => {
    vendorsRefetch();
    storesRefetch();
    taxProceduresRefetch();
    servicesRefetch();
  }, []);
  useEffect(() => {
    if (watch("storeId")?.value) {
      setPartsLoader(true);
      getParts();
    }
  }, [watch("storeId")?.value]);
  const getParts = () => {
    itemsRefetch()
      .then((res: GetItemsDropdownResponse | unknown | any) => {
        if (res?.data?.pages[0]?.success) {
          ///
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => setPartsLoader(false));
  };
  const getVendor = (vendorId: number | null) => {
    if (vendorId) {
      VendorProfileService.getVendorById(vendorId)
        .then((res: GetVendorByIdResponse) => {
          setValue("phone", (res?.data as any).phoneNumber || "");
          setValue("address", (res?.data as any).address || "");
        })
        .catch((err: any) => {
          console.error("===", err);
        });
    }
  };
  const supplierOptions = (vendorsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const storeOptions = (storesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const taxProcedureOptions = (
    taxProceduresData?.pages[0]?.data?.content || []
  ).map((item: { id: number; title: string; percentageValue?: number }) => ({
    value: item.id,
    label: item.title,
    percentageValue: item.percentageValue,
  }));
  const columns = [
    { id: "itemId" },
    { id: "itemCode" },
    { id: "unitId" },
    {
      id: "itemName",
      label: t("Item") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "supplierSKU",
      label: t("SKU"),
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "unitTitle" },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "total", label: t("Total") },
  ];
  const columnsService = [
    { id: "serviceId" },
    { id: "serviceCode" },
    {
      id: "serviceName",
      label: t("service") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "total", label: t("Total") },
  ];
  const DeleteItem = (row: any, typeName: string, index?: number) => {
    if (typeName === "purchaseOrderItems") {
      setPurchaseOrderItems((prevRows) => {
        const newRows = prevRows.filter((_, i) => i !== index);
        setValue("purchaseOrderItems", []); // clear first
        setTimeout(
          () =>
            setValue("purchaseOrderItems", newRows, {
              shouldValidate: true,
              shouldDirty: true,
            }),
          0,
        ); // reset after
        return newRows;
      });
    } else {
      setPurchaseOrderSvc((prevRows) => {
        const newRows = prevRows.filter((_, i) => i !== index);
        setValue("purchaseOrderSvc", []);
        setTimeout(
          () =>
            setValue("purchaseOrderSvc", newRows, {
              shouldValidate: true,
              shouldDirty: true,
            }),
          0,
        );
        return newRows;
      });
    }
  };
  const addNewItem = (typeName: string) => {
    if (typeName === "purchaseOrderItems") {
      const newItem = columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {},
      );
      (newItem as any).id = Date.now();
      setPurchaseOrderItems((prevRows) => [...prevRows, newItem]);
    } else {
      const newService = columnsService.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type && column.type === "number" ? 0 : "",
        }),
        {},
      );
      (newService as any).id = Date.now();
      setPurchaseOrderSvc((prevRows) => [...prevRows, newService]);
    }
  };

  const handleInputChange = (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => {
    if (typeName === "purchaseOrderItems") {
      const updatedItems = [...purchaseOrderItems];
      updatedItems[index][columnId] =
        type === "number" ? parseFloat(value) : value;
      setPurchaseOrderItems(updatedItems);
    } else {
      const updatedServices = [...purchaseOrderSvc];
      updatedServices[index][columnId] = value;
      setPurchaseOrderSvc(updatedServices);
    }
  };
  const onSubmit = (data: FormData) => {
    const filteredItems = purchaseOrderItems.filter(
      (item: any) => item?.itemName,
    );
    const filteredServices = purchaseOrderSvc.filter(
      (service: any) => service?.serviceName,
    );
    if (filteredItems.length === 0 && filteredServices.length === 0) {
      enqueueSnackbar(<AlertMessages text={t("addAtLeastOneItemService")} />, {
        variant: "error",
      });
      return;
    }
    setConfirm(true);
    handleSubmission({
      ...data,
      purchaseOrderItems: filteredItems,
      purchaseOrderSvc: filteredServices,
    });
  };
  const handleSubmission = (po: any) => {
    delete po.typeName;
    po.purchaseOrderItems.every(
      (row: any) => delete row.total && delete row.id,
    );
    po.purchaseOrderItems.every((row: any) => delete row.unitTitle);
    po.purchaseOrderSvc.every((row: any) => delete row.total && delete row.id);
    InventoryProfileService.savePurchaseOrder({
      ...po,
      supplierId: parseInt?.(po.supplierId?.value) || null,
      storeId: parseInt?.(po.storeId?.value) || null,
      taxProcedureId: parseInt?.(po?.taxProcedureId?.value) || null,
      urgencyStatus: parseInt?.(po?.urgencyStatus?.value) || null,
      stockType: parseInt(po.stockType),
      purchaseOrderSvc: po?.purchaseOrderSvc?.length
        ? po?.purchaseOrderSvc?.map((row: any) => ({
            ...row,
            serviceId: typeof row.serviceId === "string" ? null : row.serviceId,
          }))
        : [],
    })
      .then((response: NewPurchaseOrderResponse) => {
        if (response.success) {
          navigate(`/inventory/purchase/${response.data.id}`);
          setConfirm(false);
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={
                t("Purchase Order") +
                " " +
                t("created") +
                " " +
                t("successfully")
              }
            />,
            { variant: "success" },
          );
        } else {
          setConfirm(false);
          enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setConfirm(false);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      });
  };
  const calculateTotals = () => {
    const itemTotals = purchaseOrderItems.reduce(
      (acc, item) => {
        const unitPrice = parseFloat(item.unitPrice || 0);
        const quantity = parseFloat(item.quantity || 0);
        const discountPercentage = parseFloat(item.discount || 0);
        const discountAmount = (unitPrice * discountPercentage) / 100; // Calculate discount as a percentage of unitPrice
        const totalAfterDiscount = (unitPrice - discountAmount) * quantity; // Calculate total after discount

        return {
          unitPrice: acc.unitPrice + unitPrice,
          subtotal: acc.subtotal + unitPrice * quantity,
          discount: acc.discount + discountAmount * quantity, // Sum of all discounts
          total: acc.total + totalAfterDiscount, // Sum of totals after discount
        };
      },
      { unitPrice: 0, subtotal: 0, discount: 0, total: 0 },
    );

    const serviceTotals = purchaseOrderSvc.reduce(
      (acc, service) => {
        const unitPrice = parseFloat(service.unitPrice || 0);
        const quantity = parseFloat(service.quantity || 0);
        const discountPercentage = parseFloat(service.discount || 0);
        const discountAmount = (unitPrice * discountPercentage) / 100; // Calculate discount as a percentage of unitPrice
        const totalAfterDiscount = (unitPrice - discountAmount) * quantity; // Calculate total after discount

        return {
          unitPrice: acc.unitPrice + unitPrice,
          subtotal: acc.subtotal + unitPrice * quantity,
          discount: acc.discount + discountAmount * quantity, // Sum of all discounts
          total: acc.total + totalAfterDiscount, // Sum of totals after discount
        };
      },
      { unitPrice: 0, subtotal: 0, discount: 0, total: 0 },
    );

    return {
      subtotal: itemTotals.subtotal + serviceTotals.subtotal,
      discount: itemTotals.discount + serviceTotals.discount,
      taxableTotal: itemTotals.total + serviceTotals.total, // Total before tax
      total: itemTotals.total + serviceTotals.total, // Final total
    };
  };
  const totals = calculateTotals();
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
    table?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (table === "inventoryServices") {
        servicesRefetch().finally(() => setSearchLoading(null));
        return;
      }
      if (typeName === "suppliers")
        vendorsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "stores")
        storesRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "taxProcedures")
        taxProceduresRefetch().finally(() => setSearchLoading(null));
      else itemsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/inventory/purchase-orders")}
        onSubmit={() => {}}
        heading={t("Purchase Orders")}
        icon={
          <ShoppingCartOutlinedIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
      />
      <Grid
        container
        component="form"
        id="form"
        className="px-[8px]"
        spacing={2}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} sm={6} className="items-center">
            {isSubmitSuccessful ? (
              <Typography
                className="font-Saira text-[11px] font-semibold"
                style={{ color: themeColors?.primary }}
              >
                {t(steps[currentStep])}
              </Typography>
            ) : (
              <Typography
                className="font-Saira text-[15px] text-[#171826] text-left font-semibold"
                style={{ color: themeColors?.primary }}
              >
                {t("New Purchase")}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6} className="hide-on-print">
            <div className="flex gap-2 justify-end">
              <Button
                type="submit"
                disabled={confirm}
                className="primary"
                style={{ background: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => onSubmit}
              >
                {t("submit")}
              </Button>
              <Button
                className="secondary"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => navigate("/inventory")}
              >
                {t("Back to list")}
              </Button>
              {/* <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('Tutorial')}</Button> */}
            </div>
          </Grid>
        </Grid>
        <Grid item xs={12} className="mb-[16px]">
          <Divider className="bg-[#AEB3C4] hide-on-print" />
        </Grid>
        {currentStep === 0 ? (
          <>
            <Grid
              container
              item
              xs={12}
              md={6}
              spacing={2}
              className="pb-5 pt-0"
            >
              <Grid item xs={12}>
                <Typography
                  className="font-Saira text-[13px] leading-[16px] font-semibold text-left"
                  style={{ color: themeColors?.primary }}
                >
                  {t("vendor")} {t("Details")}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                className="grid"
                sx={{
                  gridTemplateColumns: checkPrivileges("ROLE_VENDOR_CREATE")
                    ? "85% 10%"
                    : "100%",
                  "& .addNew path": { fill: themeColors?.primary },
                }}
              >
                <Controller
                  name="supplierId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("vendors") + " *"}
                      defaultPlaceholder="phSelectSupplier"
                      {...field}
                      options={supplierOptions}
                      isClearable
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setTimeout(() => getVendor(selectedOption.value), 1000);
                      }}
                      error={errors.supplierId?.message as string}
                      isLoading={searchLoading === "suppliers"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "suppliers",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || dropdownPage > 1)
                            handleDropdownActions("suppliers", "searchIt", "");
                          else setDropdownSearchTerm("");
                          setDropdownPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(vendorsData?.pages[0]?.data as any).last)
                          handleDropdownActions("suppliers", "loadNext");
                      }}
                      page={dropdownPage}
                    />
                  )}
                />
                {checkPrivileges("ROLE_VENDOR_CREATE") && (
                  <Tooltip title={"Add New"}>
                    <ModalBtn
                      onClick={() => setAddNew("vendors")}
                      className="mt-5 mx-2 mb-2 cursor-pointer h-[38px] addNew"
                    />
                  </Tooltip>
                )}
                {addNew !== "" && (
                  <FormDialog
                    open={addNew !== ""}
                    onClose={(event, reason) =>
                      event && reason !== "backdropClick" ? setAddNew("") : ""
                    }
                    title={
                      addNew === "vendors"
                        ? t("addVendor")
                        : addNew === "stores"
                          ? t("addStores")
                          : addNew === "services"
                            ? t("addService")
                            : t("addTaxProcedure")
                    }
                    createPopup={true}
                    fullWidth
                    hideActions={true}
                    content={
                      addNew === "vendors" ? (
                        <SupplierModal
                          setAddNew={setAddNew}
                          vendorsRefetch={vendorsRefetch}
                        />
                      ) : addNew === "stores" ? (
                        <StoreModal
                          setAddNew={setAddNew}
                          refetch={storesRefetch}
                        />
                      ) : addNew === "services" ? (
                        <ServicesModal
                          setAddNew={setAddNew}
                          serviceRefetch={servicesRefetch}
                        />
                      ) : (
                        <TaxProcedureModal
                          setAddNew={setAddNew}
                          taxProceduresRefetch={taxProceduresRefetch}
                        />
                      )
                    }
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  type="text"
                  {...register("phone", {
                    validate: {
                      phoneLength: (value) =>
                        value === "" ||
                        (value.length >= 5 && value.length <= 17) ||
                        t("phoneLength"),
                    },
                  })}
                  placeholder={t("phPOContact")}
                  label={t("Contact")}
                  title={t("Contact")}
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message as string}
                  InputLabelProps={{ shrink: true }}
                  onChange={(
                    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                  ) => {
                    const cleaned = e.target.value.replace(/[^0-9+]/g, "");
                    setValue("phone", cleaned);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register("address")}
                  placeholder={t("phPOVendorAddress")}
                  label={t("Vendor Address")}
                  title={t("Vendor Address")}
                  fullWidth
                  error={!!errors.address}
                  helperText={errors.address?.message as string}
                  InputLabelProps={{ shrink: true }}
                  // sx={{ '& .MuiFormHelperText-contained': { bottom: '-11px !important' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  className="font-Saira text-[13px] leading-[16px] font-semibold text-left"
                  style={{ color: themeColors?.primary }}
                >
                  {t("destination")} {t("Details")}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                className="grid"
                sx={{
                  gridTemplateColumns: checkPrivileges("ROLE_STORE_CREATE")
                    ? "85% 10%"
                    : "100%",
                  "& .addNew path": { fill: themeColors?.primary },
                }}
              >
                <Controller
                  name="storeId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("stores") + " *"}
                      defaultPlaceholder="phSelectWarehouse"
                      {...field}
                      options={storeOptions}
                      isClearable
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setValue("purchaseOrderItems", []);
                        setPurchaseOrderItems([]);
                      }}
                      error={errors.storeId?.message as string}
                      isLoading={searchLoading === "stores"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions("stores", "searchIt", search);
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || dropdownPage > 1)
                            handleDropdownActions("stores", "searchIt", "");
                          else setDropdownSearchTerm("");
                          setDropdownPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(storesData?.pages[0]?.data as any).last)
                          handleDropdownActions("stores", "loadNext");
                      }}
                      page={dropdownPage}
                    />
                  )}
                />
                {checkPrivileges("ROLE_STORE_CREATE") && (
                  <Tooltip
                    onClick={() => setAddNew("stores")}
                    title={"Add New"}
                  >
                    <ModalBtn className="mt-5 mx-2 mb-2 cursor-pointer h-[38px] addNew" />
                  </Tooltip>
                )}
              </Grid>
            </Grid>
            <Grid
              container
              item
              xs={12}
              md={6}
              spacing={2}
              className="pb-5 pt-0"
            >
              <Grid item xs={12}>
                <Typography
                  className="font-Saira text-[13px] leading-[16px] font-semibold text-left"
                  style={{ color: themeColors?.primary }}
                >
                  {t("Purchase")} {t("Details")}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} className="pt-[12px]">
                <Controller
                  name="poDate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? t("Invalid Date") : true;
                    },
                  }}
                  render={({ field }) => (
                    <BasicDatePicker
                      type="datePicker"
                      {...field}
                      label={t("date") + " *"}
                      fullWidth
                      onChange={(value: any) =>
                        field.onChange(
                          new Date(value)
                            .toLocaleDateString("en-CA", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })
                            ?.split("/")
                            .reverse()
                            .join("-"),
                        )
                      }
                      required={false}
                      error={errors.poDate?.message as string}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6} className="pt-[12px]">
                <Controller
                  name="urgencyStatus"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("Priority") + " *"}
                      defaultPlaceholder="phSelectPriority"
                      {...field}
                      options={[
                        { value: 10, label: t("Low") },
                        { value: 20, label: t("Medium") },
                        { value: 30, label: t("High") },
                      ]}
                      isSearchable
                      onChange={(selectedOption: any) =>
                        field.onChange(selectedOption)
                      }
                      error={errors.urgencyStatus?.message as string}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6} className="pt-[11px]">
                <Controller
                  name="shippingDate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const date = new Date(value);
                      return isNaN(date.getTime()) && value
                        ? t("Invalid Date")
                        : true;
                    },
                  }}
                  render={({ field }) => (
                    <BasicDatePicker
                      type="datePicker"
                      {...field}
                      label={t("expectedShippingDate")}
                      fullWidth
                      onChange={(value: any) =>
                        field.onChange(
                          new Date(value)
                            .toLocaleDateString("en-CA", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })
                            ?.split("/")
                            .reverse()
                            .join("-"),
                        )
                      }
                      required={false}
                      error={errors.shippingDate?.message as string}
                      minDate={dayjs()}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6} className="pt-[11px]">
                <TextField
                  {...register("terms")}
                  placeholder={t("phPOTerms")}
                  label={t("Terms")}
                  title={t("Terms")}
                  fullWidth
                  error={!!errors.terms}
                  helperText={errors.terms?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {/* <Grid item xs={12}>
              <Controller
                name="stockType"
                control={control}
                rules={{ required: t("This field is required.") }}
                render={({ field }) => (
                  <>
                    <label className="text-left block  m-0">
                      {t("Stock Type") + " *"}
                    </label>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      {...field}
                    >
                      <FormControlLabel
                        value={4}
                        control={
                          <Radio
                            sx={{
                              "& svg:nth-child(2) path": {
                                fill: themeColors?.primary,
                              },
                            }}
                          />
                        }
                        label={t("Stock First")}
                        sx={{
                          "& .MuiTypography-root": {
                            position: "relative",
                            top: "-8px",
                          },
                        }}
                      />
                      <FormControlLabel
                        value={5}
                        control={
                          <Radio
                            sx={{
                              "& svg:nth-child(2) path": {
                                fill: themeColors?.primary,
                              },
                            }}
                          />
                        }
                        label={t("Invoice First")}
                        sx={{
                          "& .MuiTypography-root": {
                            position: "relative",
                            top: "-8px",
                            whiteSpace: "nowrap",
                            width: "100px",
                          },
                        }}
                      />
                    </RadioGroup>
                    {errors.stockType?.message && (
                      <Typography
                        color="error"
                        variant="caption"
                        className="font-Saira text-[11px] leading-[18px] font-medium italic text-left block text-[#EF5350] absolute mt-[-10px]"
                      >
                        {errors.stockType?.message as string}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Grid> */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="taxable"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <CommonCheckbox
                      {...field}
                      label={t("Tax Exclusive")}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        field.onChange(event.target.checked);
                        if (!event.target.checked) {
                          resetField("taxProcedureId");
                          setTaxPercentage(0);
                        }
                      }}
                      checked={field.value}
                      color={themeColors?.primary}
                    />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                className="grid"
                sx={{
                  gridTemplateColumns: checkPrivileges(
                    "ROLE_TAXPROCEDURE_CREATE",
                  )
                    ? "85% 10%"
                    : "100%",
                  "& .addNew path": { fill: themeColors?.primary },
                }}
              >
                {watch("taxable") ? (
                  <>
                    <Controller
                      name="taxProcedureId"
                      control={control}
                      defaultValue={null}
                      rules={{
                        required: watch("taxable")
                          ? "This field is required."
                          : false,
                      }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("Tax Procedures") + " *"}
                          defaultPlaceholder="phSelectTaxProcedure"
                          {...field}
                          options={taxProcedureOptions}
                          isClearable
                          isSearchable
                          onChange={(selectedOption: any) => {
                            field.onChange(selectedOption);
                            setTaxPercentage(
                              selectedOption.percentageValue / 100,
                            );
                          }}
                          error={errors.taxProcedureId?.message as string}
                          isLoading={searchLoading === "taxProcedures"}
                          onInputChange={(
                            search: string,
                            { action }: { action: string },
                          ) => {
                            if (
                              action === "input-change" ||
                              (action !== "set-value" &&
                                action !== "menu-close" &&
                                action !== "input-blur")
                            )
                              handleDropdownActions(
                                "taxProcedures",
                                "searchIt",
                                search,
                              );
                            else if (
                              action === "input-blur" ||
                              action === "menu-close"
                            ) {
                              if (dropdownSearchTerm || dropdownPage > 1)
                                handleDropdownActions(
                                  "taxProcedures",
                                  "searchIt",
                                  "",
                                );
                              else setDropdownSearchTerm("");
                              setDropdownPage(1);
                            }
                          }}
                          onMenuScrollToBottom={() => {
                            if (
                              !(taxProceduresData?.pages[0]?.data as any).last
                            )
                              handleDropdownActions(
                                "taxProcedures",
                                "loadNext",
                              );
                          }}
                          page={dropdownPage}
                        />
                      )}
                    />
                    {checkPrivileges("ROLE_TAXPROCEDURE_CREATE") && (
                      <Tooltip
                        onClick={() => setAddNew("taxprocedures")}
                        title={"Add New"}
                      >
                        <ModalBtn className="mt-5 mx-2 mb-2 cursor-pointer h-[38px] addNew" />
                      </Tooltip>
                    )}
                  </>
                ) : null}
              </Grid>
            </Grid>
            <Grid item xs={12} className="pt-0">
              <Divider className="bg-[#AEB3C4] hide-on-print" />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("comments")}
                placeholder={t("phPOComments")}
                label={t("Comments")}
                title={t("Comments")}
                fullWidth
                error={!!errors.comments}
                helperText={errors.comments?.message as string}
                InputLabelProps={{ shrink: true }}
                multiline
                rows={2}
              />
            </Grid>
          </>
        ) : (
          ""
        )}
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            {partsLoader ? (
              <Box sx={{ "& .MuiBackdrop-root": { zIndex: 999 } }}>
                <Loader />
              </Box>
            ) : null}
            <NewPurchaseSteps
              form={{
                control,
                register,
                handleSubmit,
                setValue,
                errors,
                isSubmitSuccessful,
                watch,
                resetField,
              }}
              addNewItem={addNewItem}
              handleInputChange={handleInputChange}
              items={itemsData?.pages[0]?.data?.content || []}
              services={servicesData?.pages[0]?.data?.content || []}
              DeleteItem={DeleteItem}
              {...{
                currentStep,
                setCurrentStep,
                columns,
                columnsService,
                purchaseOrderItems,
                purchaseOrderSvc,
                loggedInUserCurrency,
                t,
                themeColors,
                setAddNew,
                dropdownPage,
                searchLoading,
                dropdownSearchTerm,
                setDropdownSearchTerm,
                setDropdownPage,
              }}
              itemsData={itemsData || {}}
              handleDropdownActions={handleDropdownActions}
              servicesData={servicesData || {}}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left">
            {t("Purchase Order Memo")}
          </Typography>
        </Grid>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              {...register("poMemo")}
              placeholder={t("phPOMemo")}
              title={t("Purchase Order Memo")}
              fullWidth
              error={!!errors.poMemo}
              helperText={errors.poMemo?.message as string}
              InputLabelProps={{ shrink: true }}
              multiline
              rows={5}
              sx={{
                "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                  width: "100%",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              className="bg-[#D8D1E0] rounded-[8px]"
              sx={{
                border: "1px solid #5f6770",
                width: "calc(100% + 8px)",
                padding: "8px 14px",
              }}
            >
              <Box className="flex justify-end gap-12 mt-[9px]">
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                  {t("Before Discount")}
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                  <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                  {new Intl.NumberFormat("en-US", {}).format(totals.subtotal)}
                </Typography>
              </Box>
              <Box className="flex justify-end gap-12 mt-[9px]">
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                  {t("Discount")}
                  {/* {t("Discount") + " %"} */}
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                  <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                  {new Intl.NumberFormat("en-US", {}).format(totals.discount)}
                </Typography>
              </Box>
              <Box className="flex justify-end gap-12 mt-[9px]">
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                  {t("tax") + ` (${taxPercentage * 100}%)`}
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                  <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                  {new Intl.NumberFormat("en-US", {}).format(
                    totals.taxableTotal * taxPercentage || 0,
                  )}
                </Typography>
              </Box>
              <Box className="flex justify-end gap-12 mt-[9px]">
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[auto]">
                  {t("totalWithTax")}
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[20%] text-end">
                  <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                  {new Intl.NumberFormat("en-US", {}).format(
                    totals.total + totals.taxableTotal * taxPercentage,
                  )}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} className="hide-on-print">
          <Divider className="bg-[#AEB3C4] mt-[4px]" />
        </Grid>
        <Grid
          container
          item
          xs={12}
          spacing={2}
          className="items-center hide-on-print"
        >
          <Grid item xs={12} sm={6} className="text-start"></Grid>
          <Grid item xs={12} sm={6}>
            <div className="flex gap-2 justify-end">
              <Button
                type="submit"
                disabled={confirm}
                className="primary"
                style={{ background: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => onSubmit}
              >
                {t("submit")}
              </Button>
              <Button
                className="secondary"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => navigate("/inventory")}
              >
                {t("Back to list")}
              </Button>
              {/* <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('Tutorial')}</Button> */}
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

const steps = [
  "Purchase Order",
  "supplierBill",
  "supplierPayment",
  "Stock Received",
  // "Debit Note",
  // "Unstock",
];

interface NewPurchaseStepsProps {
  form: FormData;
  addNewItem: (typeName: string) => void;
  handleInputChange: (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => void;
  currentStep: number;
  setCurrentStep: any;
  columns: any[];
  columnsService: any[];
  purchaseOrderItems: any[];
  purchaseOrderSvc: any[];
  items: any[];
  services: any[];
  loggedInUserCurrency?: string;
  DeleteItem: (row: any, typeName: string) => void;
  t?: any;
  themeColors?: any;
  setAddNew?: any;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  dropdownPage: number;
  searchLoading: string | null;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  setDropdownPage: any;
  itemsData: any;
  servicesData: any;
}

export const NewPurchaseSteps: React.FC<NewPurchaseStepsProps> = ({
  form,
  addNewItem,
  handleInputChange,
  currentStep,
  columns,
  columnsService,
  purchaseOrderItems,
  purchaseOrderSvc,
  items,
  services,
  loggedInUserCurrency,
  DeleteItem,
  t,
  themeColors,
  setAddNew,
  servicesData,
  handleDropdownActions,
  itemsData,
  dropdownPage,
  searchLoading,
  dropdownSearchTerm,
  setDropdownSearchTerm,
  setDropdownPage,
}) => {
  return (
    <>
      <Stepper
        className="hide-on-print"
        activeStep={currentStep}
        connector={<></>}
        sx={{
          "& .MuiStep-horizontal .Mui-active + .MuiStepLabel-labelContainer": {
            background: `linear-gradient(20.78deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
          },
        }}
      >
        {steps.map((label, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: { optional?: React.ReactNode } = {};
          return (
            <Step key={index} {...stepProps}>
              <StepLabel {...labelProps}>{t(label)}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box className="bg-white py-[12px] px-[18px] rounded-[8px] mt-[16px] relative">
        <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left">
          {t(steps[currentStep])}
        </Typography>
        <InventoryItemsTable
          form={form}
          columns={columns}
          handleInputChange={handleInputChange}
          rows={purchaseOrderItems}
          typeName="purchaseOrderItems"
          items={items}
          currency={loggedInUserCurrency}
          DeleteItem={DeleteItem}
          itemsResponse={itemsData || {}}
          handleDropdownActions={handleDropdownActions}
          {...{
            dropdownPage,
            searchLoading,
            dropdownSearchTerm,
            setDropdownSearchTerm,
            setDropdownPage,
          }}
        />
        <Grid item xs={12} sm={6} className="hide-on-print">
          <div className="flex gap-2 justify-start">
            <Button
              className="secondary flex gap-1"
              style={{ color: themeColors?.primary }}
              variant="contained"
              disableElevation
              onClick={() => addNewItem("purchaseOrderItems")}
            >
              <AddIcon className="w-[20px] h-[20px]" /> {t("Add More Items")}
            </Button>
            {/* <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('Import')}</Button>
                    <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t("Export")}</Button> */}
          </div>
        </Grid>
      </Box>
      <Box className="flex gap-2 justify-start mt-[16px]">
        <Button
          className="secondary cursor-text"
          style={{ color: themeColors?.primary }}
          variant="contained"
          disableElevation
        >
          {t("Additional Costs")}
        </Button>
      </Box>
      <Box className="bg-white py-[12px] px-[18px] rounded-[8px] mt-[16px]">
        <Box className="flex justify-between items-center">
          <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left">
            {t("Additional Cost")}
          </Typography>
          {checkPrivileges("ROLE_SERVICE_CREATE") ? (
            <Button
              className="secondary flex gap-1"
              style={{ color: themeColors?.primary, padding: "10px" }}
              variant="contained"
              disableElevation
              onClick={() => setAddNew("services")}
            >
              <AddIcon className="w-[15px] h-[15px]" /> {t("addNewService")}
            </Button>
          ) : null}
        </Box>
        <InventoryServicesTable
          form={form}
          columns={columnsService}
          handleInputChange={handleInputChange}
          rows={purchaseOrderSvc}
          typeName="purchaseOrderSvc"
          services={services}
          currency={loggedInUserCurrency}
          DeleteItem={DeleteItem}
          servicesResponse={servicesData || {}}
          handleDropdownActions={handleDropdownActions}
          {...{
            dropdownPage,
            searchLoading,
            dropdownSearchTerm,
            setDropdownSearchTerm,
            setDropdownPage,
          }}
        />
        <Grid item xs={12} sm={6} className="hide-on-print">
          <div className="flex gap-2 justify-start">
            <Button
              className="secondary flex gap-1"
              style={{ color: themeColors?.primary }}
              variant="contained"
              disableElevation
              onClick={() => addNewItem("purchaseOrderSvc")}
            >
              <AddIcon className="w-[20px] h-[20px]" /> {t("Add More Items")}
            </Button>
          </div>
        </Grid>
        <Grid item xs={12}>
          <Divider className="bg-[#AEB3C4] mt-[15px] mb-[8px]" />
        </Grid>
      </Box>
    </>
  );
};
